import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { domeLight, type Ellipse, fromPath, type HeadPicture, inEllipse, mask, type SetFlair, toneOf } from './mythic';

/**
 * The Tempest set (ADR-0160, mythic): a thunderhead for a head, storm plate under a scarf in the wind, and the
 * Thunderclap gun.
 *
 * The head is a storm cloud (a mask over the whole head): slate puffs piled up, each lit along its top-left and dark in
 * its belly, a flat black underside, two angry eyes of the team's light under it and a fork of lightning down its side.
 * The plate is storm-grey steel edged in gold, a collar of cloud at the neck, a bolt of gold on the breast and the
 * team's sash across it. Its flair is weather: bolts jumping over the cloud and from the plate to the gun, the cloud
 * flashing from inside, rain off its underside, a scarf in the team's colour whose two tails are never still (chains of
 * links in a wind of their own), sparks off its heels.
 *
 * Rubric (`assetc mythic` at 5× and 1:1 on grass, lane and night, both teams, idle, run and dead; the admin app's Skins
 * page, both previews; the `mythic-tempest` playtest: the Store, PLAY's stage, a round at 1920×1080): cloud S5 R4 P4 L4
 * C4 A4 F4, plate S4 R4 P4 L4 C4 F4. Still weak: the cloud's puffs are slate on slate at 1:1 (its lit rims and its eyes
 * carry it); the plate's bolt is behind the gun aimed right; its flourish's bolt is the Magic Wand's cyan, not gold.
 */

// --- The thunderhead -------------------------------------------------------------------------------------------------

/** Slate by the light on it: a pale rim where a puff faces the light, then darker and darker into its belly. */
const SLATE = [
  ['5', 0.93],
  ['4', 0.66],
  ['3', 0.3],
  ['2', -0.12],
  ['1', -0.5],
] as const;
/** The cloud's puffs from the head's middle (u forward, v down), back to front. */
const PUFFS: readonly Ellipse[] = [
  [-9, -12, 7.5, 7.5],
  [10, -13, 7, 7],
  [-17.5, 4, 7, 7],
  [19, 6, 6.2, 6.2],
  [-10, -3, 11.5, 11.5],
  [13, -2, 10.5, 10.5],
  [2, -8, 12.5, 12.5],
  [9, 8, 10, 9],
  [-4, 6, 12.5, 11.5],
];
/** The eyes: [middle u, middle v, half-width, how the brow slants down to the nose]. */
const EYES: readonly (readonly [number, number, number, number])[] = [
  [-2, 3, 3.8, 0.55],
  [10.6, 3, 2.9, -0.55],
];
/** A fork of lightning down the cloud's back. */
const FORK = [-15, -8, -11, -1, -14.5, 0, -9.5, 9];
const BRANCH = [-12.6, 0.4, -17, 5];

const thunderhead: HeadPicture = (u, v, lx, ly) => {
  let puff: Ellipse | null = null;
  for (const p of PUFFS) if (inEllipse(u, v, p) <= 1) puff = p;
  if (!puff) return null;
  for (const [eu, ev, half, slant] of EYES) {
    const du = u - eu;
    if (Math.abs(du) >= half) continue;
    // An angry eye: a lens cut off by a brow slanting down to the nose; white-hot, the team's light round it.
    const brow = ev - 2.4 + du * slant + 1.2;
    const e = (du / half) ** 2 + ((v - ev) / 2.9) ** 2;
    if (e < 1 && v >= brow) return e < 0.4 && v >= brow + 0.8 ? 'w' : 'c';
    if (e < 1.5 && v < brow && v > brow - 1.6) return 'k';
  }
  if (fromPath(u, v, FORK) < 0.75 || fromPath(u, v, BRANCH) < 0.6) return 'y';
  const l = domeLight(u, v, puff, lx, ly);
  // Its underside: flat and black, where the rain would hang.
  return toneOf(l - (v > 12.5 ? 0.5 : v > 9 ? 0.2 : 0), SLATE, 'k');
};

// --- Storm plate -----------------------------------------------------------------------------------------------------

/**
 * The plate standing (the frame's x 7-52, y 41-55): a breastplate of storm-grey steel lit along its top-left, round
 * pauldrons edged in gold, a collar of cloud at the neck, a gold bolt on the breast and the team's sash from the near
 * shoulder down across it.
 */
function plate(): Part {
  const [x0, y0] = [7, 41];
  const c = new PixelCanvas(46, 15);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  for (let y = 44; y <= 55; y++)
    for (let x = 16; x <= 43; x++) {
      const edge = x === 16 || y === 44;
      at(x, y, edge ? '5' : x <= 18 || y === 45 ? '4' : x >= 41 || y >= 54 ? '2' : '3');
    }
  for (const cx of [12.5, 47]) {
    const e: Ellipse = [cx, 47.5, 5.6, 4.6];
    for (let y = 42; y <= 53; y++)
      for (let x = 6; x <= 53; x++) {
        const r = inEllipse(x + 0.5, y + 0.5, e);
        if (r > 1) continue;
        const l = -(x + 0.5 - cx) / 5.6 - (y + 0.5 - 47.5) / 4.6;
        at(x, y, r > 0.66 ? (l > -0.2 ? 'y' : 'Y') : l > 0.45 ? '5' : l > -0.3 ? '4' : '3');
      }
  }
  // The team's sash, from the near shoulder down across the breast.
  for (let x = 16; x <= 43; x++) {
    const y = 45 + (x - 16) * 0.36;
    for (let k = 0; k < 4; k++) at(x, Math.round(y) + k, k === 0 ? 'c' : k === 3 ? 'B' : 'b');
  }
  // A bolt of gold on the breast.
  c.stamp(
    `
    ..yy
    .yy.
    yyyy
    ..yY
    .yY.
    yY..
  `,
    19 - x0,
    49 - y0,
  );
  // The collar of cloud: three puffs round the neck.
  for (const [cx, cy, r] of [
    [24.5, 43.6, 3],
    [30.5, 44.2, 3.2],
    [36.5, 43.6, 3],
  ] as const)
    for (let y = 41; y <= 47; y++)
      for (let x = 20; x <= 40; x++)
        if (Math.hypot(x + 0.5 - cx, y + 0.5 - cy) <= r)
          at(x, y, x + 0.5 - cx + (y + 0.5 - cy) < -1.2 ? 'w' : y + 0.5 - cy > 1.2 ? '4' : '5');
  return [c, x0, y0];
}

/** The plate lying (right of the legs, under the head): the sash across the steel, the collar of cloud at the neck. */
const DEAD_PLATE = grid(`
  ....5544
  ...54433
  25443333
  2443333c
  244333cb
  24433cbb
  2443cbbB
  244cbbB3
  24cbbB33
  2cbbB333
  2bbB3333
  2bB3yy33
  2B33y333
  2433yy33
  243333w.
  2433355w
  22225555
  ....4444
`);

/** Steel sabatons under the team's trousers. */
const STORM_PLATE = (): Outfit => ({ torso: plate(), dead: [DEAD_PLATE, 24, 51], legs: { n: '4', N: '2' } });

// --- Thunderclap on every other gun ----------------------------------------------------------------------------------

/**
 * The Thunderclap finish (the default gun is its own, `pollinator_tempest`): storm-grey where the wood was, the brass
 * struck bright (gold to white); the steel as it is.
 */
const THUNDERCLAP = { N: '1', n: '2', m: '3', t: '4', e: '5', U: 'Y', Y: 'y', y: 'w' } as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
const BOLT = [WHITE, 0xfee761] as const;

/** A tail of the scarf (`k` 0 the upper, 1 the lower): a ribbon in the team's colour, gold at its end, never still. */
const scarf = (k: number) =>
  ({
    kind: 'chain',
    at: { on: 'torso', x: 15, y: 44 + k * 2 },
    n: 10,
    seg: 2.7,
    dir: Math.PI * (0.92 + k * 0.14),
    curl: k ? -0.03 : 0.03,
    stiff: 4.5,
    g: 90,
    drag: 0.05,
    sway: [0.22, 0.55 + k * 0.17, k * 0.4],
    wind: 2600,
    w: [5, 5, 3],
    shape: 'dots',
    tones: ['team', 'team', 'team', 'team', 'teamLight', 0xfeae34],
    rim: 'teamDark',
    layer: 'back',
  }) as const;

const FLAIR: SetFlair = {
  head: [
    // The cloud flashing from inside.
    {
      kind: 'pulse',
      at: { on: 'head', x: 30, y: 26 },
      r: [18, 24],
      alpha: [0.02, 0.22],
      period: 0.47,
      tone: 0xfee761,
      layer: 'back',
    },
    // Its eyes burn in the team's light.
    {
      kind: 'pulse',
      at: { on: 'head', x: 28, y: 31.5 },
      r: [4, 6.5],
      alpha: [0.3, 0.6],
      period: 1.1,
      tone: 'teamLight',
      layer: 'front',
    },
    {
      kind: 'pulse',
      at: { on: 'head', x: 40.5, y: 31.5 },
      r: [3.5, 5.5],
      alpha: [0.3, 0.6],
      period: 1.1,
      tone: 'teamLight',
      layer: 'front',
    },
    // Bolts jumping over it.
    {
      kind: 'arcs',
      pts: [
        { on: 'head', x: 8, y: 32 },
        { on: 'head', x: 20, y: 10 },
        { on: 'head', x: 40, y: 8 },
        { on: 'head', x: 53, y: 30 },
        { on: 'head', x: 30, y: 46 },
        { on: 'head', x: 14, y: 44 },
        { on: 'head', x: 46, y: 44 },
      ],
      every: [0.22, 0.7],
      life: 0.17,
      jag: 4.5,
      tones: BOLT,
      glow: 10,
      layer: 'front',
    },
    // Rain off its underside, behind the shoulders.
    {
      kind: 'emit',
      at: { on: 'head', x: 30, y: 44 },
      box: [44, 3],
      rate: 22,
      v: [-6, -2, 70, 100],
      carry: 0.3,
      g: 220,
      life: [0.22, 0.34],
      size: [1, 1],
      tall: 3,
      tones: [0xc0cbdc, 0x8b9bb4],
      alpha: [0.95, 0.6],
      layer: 'back',
    },
  ],
  outfit: [
    // The scarf: two long tails in the team's colour from the neck, whipped about by a wind of their own.
    scarf(0),
    scarf(1),
    // Bolts from the plate to the ground and the gun.
    {
      kind: 'arcs',
      pts: [
        { on: 'torso', x: 14, y: 46 },
        { on: 'torso', x: 28, y: 52 },
        { on: 'feet', x: 20, y: 67 },
        { on: 'feet', x: 42, y: 67 },
        { on: 'gun', x: 40, y: 14 },
        { on: 'gun', x: 58, y: 20 },
      ],
      every: [0.45, 1.2],
      life: 0.15,
      jag: 3.5,
      tones: BOLT,
      glow: 8,
      layer: 'front',
    },
    // Sparks off its heels on the run.
    {
      kind: 'emit',
      at: { on: 'feet', x: 30, y: 68 },
      box: [12, 2],
      rate: 0,
      run: 0.06,
      v: [-30, 30, -60, -20],
      g: 260,
      life: [0.14, 0.3],
      size: [2, 1],
      tones: [WHITE, 0xfee761, 0xfeae34],
      add: true,
      glow: 4,
      layer: 'back',
    },
  ],
  gun: [
    // The coil's arc between the prongs, and its light.
    {
      kind: 'arcs',
      pts: [
        { on: 'gun', x: 56, y: 14 },
        { on: 'gun', x: 56, y: 26 },
        { on: 'gun', x: 63, y: 20 },
        { on: 'gun', x: 44, y: 12 },
      ],
      every: [0.07, 0.22],
      life: 0.11,
      jag: 2,
      tones: BOLT,
      layer: 'gun',
    },
    {
      kind: 'pulse',
      at: { on: 'gun', x: 57, y: 20 },
      r: [5, 9],
      alpha: [0.1, 0.4],
      period: 0.31,
      tone: 0xfee761,
      layer: 'gun',
    },
  ],
};

/** The Tempest set (ADR-0160). */
export const TEMPEST = drawnLater({
  head: () => mask(thunderhead),
  outfit: STORM_PLATE,
  gun: THUNDERCLAP,
  flair: FLAIR,
});
